import React, { useMemo, useState, useEffect } from "react";
import { Col } from "reactstrap";
import CustomDataTable from "components/customs/table";
import {
  AddButton,
  FormLabel,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import { getFormattedNumber, Toast } from "utility/helpers";
import styled from "styled-components";
import CommonModal from "components/common/modal";
import FormGenerator from "components/customs/form-generator/input";
import {
  genericPost,
  genericGet,
  genericPut,
  genericDelete,
} from "services/services.general";
import { getAccessToken, getPassCode, getUserDetails } from "config";
import Swal from "sweetalert2";
import { ACCOUNT_OTHERS } from "utility/constants";
import dpwhColumns from "./dpwhColumns";

const TableContainer = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const TableTitle = styled.h4`
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black900};
`;

const TotalCostContainer = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.black100};
  display: flex;
  justify-content: flex-end;
`;

const TotalCostText = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black900};
`;

const ModalFormRow = styled.div`
  margin-top: 1.5rem;
`;

const ModalButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const DpwhTable = ({
  onAdd,
  onEdit,
  onDelete,
  budgetAllocation = 0,
  recipientId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const role = getUserDetails()?.type;

  const [formData, setFormData] = useState({
    title: "",
    budget: "",
    notes: "",
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      // Edit mode
      setEditingItem(item);

      setFormData({
        title: item.title || "",
        budget: item.budget || "",
        notes: item.notes || "",
      });
    } else {
      // Add mode
      setEditingItem(null);

      setFormData({
        title: "",
        budget: "",
        notes: "",
      });
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);

    setFormData({
      title: "",
      budget: "",
      notes: "",
    });
  };

  const fetchItems = () => {
    const passCode = getPassCode();

    setIsLoading(true);

    const itemsUrl = passCode
      ? `/dpwhitem/${passCode}/items`
      : `/recipient/${recipientId}/sub-dpwh-items`;

    genericGet({
      url: itemsUrl,
      headers: {
        "x-auth-token": getAccessToken(),
      },
      callback: (responseData) => {
        setIsLoading(false);

        if (responseData) {
          // Handle response - adjust based on actual API response structure
          let fetchedItems = [];

          if (Array.isArray(responseData)) {
            fetchedItems = responseData;
          } else if (responseData?.rows && Array.isArray(responseData.rows)) {
            fetchedItems = responseData.rows;
          } else if (responseData?.data && Array.isArray(responseData.data)) {
            fetchedItems = responseData.data;
          } else if (responseData?.items && Array.isArray(responseData.items)) {
            fetchedItems = responseData.items;
          }

          setItems(fetchedItems);
        } else {
          setItems([]);
        }
      },
    });
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const totalBudget = useMemo(() => {
    if (!Array.isArray(items)) {
      return 0;
    }

    return items.reduce((sum, item) => {
      const budget = Number(item.budget) || 0;

      return sum + budget;
    }, 0);
  }, [items]);

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      Toast({
        type: 4,
        content: "Project Allocation Title is required",
      });

      return;
    }

    const newItemBudget = formData.budget ? Number(formData.budget) : 0;

    if (newItemBudget <= 0) {
      Toast({
        type: 4,
        content: "Budget must be greater than 0",
      });

      return;
    }

    // When editing, exclude the current item's budget from the total
    const existingTotalBudget = editingItem
      ? totalBudget - (Number(editingItem.budget) || 0)
      : totalBudget;
    const totalWithNewItem = existingTotalBudget + newItemBudget;
    const budgetAllocationValue = Number(budgetAllocation) || 0;

    if (totalWithNewItem > budgetAllocationValue) {
      Toast({
        type: 4,
        content: `Total estimated cost (${getFormattedNumber(
          totalWithNewItem
        )}) exceeds the budget allocation (${getFormattedNumber(
          budgetAllocationValue
        )})`,
      });

      return;
    }

    const passCode = getPassCode();

    if (!passCode) {
      Toast({
        type: 4,
        content: "Pass code not found. Please login using code login.",
      });

      return;
    }

    setIsSubmitting(true);

    const submitData = {
      recipientId,
      title: formData.title,
      budget: newItemBudget,
      notes: formData.notes || "",
    };

    if (editingItem) {
      // Update existing item
      genericPut({
        url: `/dpwhitem/${editingItem.id}`,
        formData: submitData,
        headers: {
          "x-auth-token": getAccessToken(),
        },
        callback: ({ isSuccess, msg }) => {
          setIsSubmitting(false);

          if (isSuccess) {
            Toast({
              type: 1,
              content: "Project updated successfully",
            });

            if (onEdit) {
              onEdit(submitData);
            }

            // Refetch items to get the updated list
            fetchItems();

            handleCloseModal();
          } else {
            Toast({
              type: 4,
              content: msg || "Failed to update project",
            });
          }
        },
      });
    } else {
      // Create new item
      genericPost({
        url: "/dpwhitem",
        formData: submitData,
        headers: {
          "x-auth-token": getAccessToken(),
        },
        callback: ({ isSuccess, msg }) => {
          setIsSubmitting(false);

          if (isSuccess) {
            Toast({
              type: 1,
              content: "Project added successfully",
            });

            if (onAdd) {
              onAdd(submitData);
            }

            // Refetch items to get the updated list
            fetchItems();

            handleCloseModal();
          } else {
            Toast({
              type: 4,
              content: msg || "Failed to add project",
            });
          }
        },
      });
    }
  };

  const handleAddClick = () => {
    if (onAdd) {
      handleOpenModal();
    }
  };

  const handleEditClick = (item) => {
    if (onEdit) {
      handleOpenModal(item);
    }
  };

  const handleDeleteClick = (item) => {
    if (!item || !item.id) {
      return;
    }

    Swal.fire({
      title: "Warning",
      text: "Are you sure you want to delete this project?",
      icon: "warning",
      confirmButtonText: "OK",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setIsDeleting(true);

        genericDelete({
          url: `/dpwhitem/${item.id}`,
          headers: {
            "x-auth-token": getAccessToken(),
          },
          callback: ({ isSuccess }) => {
            setIsDeleting(false);

            if (isSuccess) {
              Toast({
                type: 1,
                content: "Project deleted successfully",
              });

              if (onDelete) {
                onDelete(item);
              }

              // Refetch items to get the updated list
              fetchItems();
            } else {
              Toast({
                type: 4,
                content: "Failed to delete project",
              });
            }
          },
        });
      }
    });
  };

  const columns = dpwhColumns({
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
  });

  return (
    <>
      <TableContainer>
        <TableHeader>
          <TableTitle>
            {"Current Infrastructure Projects under this allocation"}
          </TableTitle>
          {role === ACCOUNT_OTHERS && (
            <AddButton onClick={handleAddClick}>{"Add"}</AddButton>
          )}
        </TableHeader>

        <CustomDataTable
          columns={columns}
          data={items}
          noHeader
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          progressPending={isLoading}
        />

        <TotalCostContainer>
          <TotalCostText>
            {`Total Estimated Cost: ${getFormattedNumber(totalBudget)}`}
          </TotalCostText>
        </TotalCostContainer>
      </TableContainer>

      <CommonModal
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        title={editingItem ? "Edit Project" : "Add New Project"}
        modalSize="md"
        renderBody={
          <>
            <ModalFormRow>
              <Col xs="12">
                <FormLabel>{"PROJECT ALLOCATION TITLE"}</FormLabel>
                <FormGenerator
                  type="text"
                  placeholder="New Project"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  isDisabled={!!editingItem}
                  readOnly={!!editingItem}
                />
              </Col>
            </ModalFormRow>

            <ModalFormRow>
              <Col xs="12">
                <FormLabel>{"BUDGET"}</FormLabel>
                <FormGenerator
                  type="number"
                  placeholder="P0"
                  value={formData.budget}
                  onChange={(value) => {
                    const budgetValue = value?.floatValue || value?.value || "";

                    setFormData({ ...formData, budget: budgetValue });
                  }}
                />
              </Col>
            </ModalFormRow>

            <ModalFormRow>
              <Col xs="12">
                <FormLabel>{"NOTES"}</FormLabel>
                <FormGenerator
                  type="textarea"
                  placeholder=""
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={4}
                />
              </Col>
            </ModalFormRow>

            <ModalButtonsContainer>
              <StyledCancelButton
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                {"Cancel"}
              </StyledCancelButton>
              <StyledSaveButton
                onClick={handleSubmit}
                disabled={isSubmitting || isDeleting}
              >
                {(() => {
                  if (isSubmitting) {
                    return editingItem ? "Updating..." : "Submitting...";
                  }

                  return editingItem ? "Update" : "Submit";
                })()}
              </StyledSaveButton>
            </ModalButtonsContainer>
          </>
        }
      />
    </>
  );
};

export default DpwhTable;
